import React, {
    ReactNode,
    createContext,
    useContext,
    useEffect,
    useState
} from 'react';

export enum Page {
    Issues = 'issues',
    Dashboard = 'dashboard',
    Table = "table"
}

interface PageNavigationState {
    currentPage: Page;
    searchParams: URLSearchParams;
    navigateToPage: (page: Page, searchParams?: URLSearchParams, isEphemeral?: boolean) => void;
}

const PageNavigationContext = createContext<PageNavigationState | undefined>(undefined);

export const PageNavigationContextProvider: React.FC<{ children: ReactNode; }> = ({ children }) => {
    const [currentPage, setCurrentPage] = useState<Page>(() => {
        if (typeof window === 'undefined') {
            return Page.Dashboard;
        }
        const url = new URL(window.location.href);
        const page = url.searchParams.get('page');
        return page as Page || Page.Dashboard;
    });

    const [searchParams, setSearchParams] = useState<URLSearchParams>(() => {
        if (typeof window === 'undefined') {
            return new URLSearchParams();
        }
        const url = new URL(window.location.href);
        return url.searchParams;
    });

    useEffect(() => {
        const handlePopState = () => {
            const url = new URL(window.location.href);
            const page = url.searchParams.get('page') as Page || Page.Dashboard;
            const searchParams = url.searchParams;
            setCurrentPage(page);
            setSearchParams(searchParams);
        };

        window.addEventListener('popstate', handlePopState);

        return () => {
            window.removeEventListener('popstate', handlePopState);
        };
    }, []);

    const navigateToPage = (page: Page, searchParams?: URLSearchParams, isEphemeral?: boolean) => {
        const currentUrl = new URL(window.location.href);
        const urlWithoutSearchParams = `${currentUrl.origin}${currentUrl.pathname}`;
        const newUrl = new URL(urlWithoutSearchParams);
        newUrl.searchParams.set('page', page);
        if (searchParams) {
            searchParams.forEach((value, key) => {
                newUrl.searchParams.set(key, value);
            });
        }
        if (isEphemeral) {
            window.history.replaceState({}, '', newUrl.toString());
        } else {
            window.history.pushState({}, '', newUrl.toString());
        }
        setCurrentPage(page);
        setSearchParams(searchParams || new URLSearchParams());
    };

    return (
        <PageNavigationContext.Provider value={{ currentPage, searchParams, navigateToPage }}>
            {children}
        </PageNavigationContext.Provider>
    );
};

export const usePageNavigation = (): PageNavigationState => {
    const context = useContext(PageNavigationContext);
    if (!context) {
        throw new Error('usePageNavigation must be used within a PageNavigationProvider');
    }
    return context;
};